import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import { fetchNewsPageData } from '../../store/news/actions';
import { getNewsPageLoadingError } from '../../store/news/selectors';
import {
  isLoadingStream,
  getMessages,
  hasAttemptedLoad,
} from '../../store/activity-stream/selectors';
import { getClubSections } from '../../store/clubs/selectors';
import { withTranslation } from '../../lib/i18n';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import ClubRouter from '../../lib/routing';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import News from '../../components/news';

const validCategories = ['album', 'news', 'club_event', 'match_preview', 'match_report', 'video'];

class NewsLandingPage extends React.Component {
  static async getInitialProps({ isServer, club, query, reduxStore, res, req }) {
    const { teamId, month: queryMonth, category } = query;
    let month;
    const queryMonthRegex = /^[0-9]{4}-[0-9]{2}/;
    if (queryMonth && queryMonthRegex.exec(queryMonth)) {
      const [y, m] = queryMonth.split('-');
      month = DateTime.fromObject({
        year: parseInt(y, 10),
        month: parseInt(m, 10),
        day: 1,
      }).toFormat('yyyy-MM');
    }

    const initialProps = {
      category: validCategories.includes(category) ? category : undefined,
      clubId: club.id,
      month,
      teamId: teamId ? parseInt(teamId, 10) : null,
      namespacesRequired: ['common', 'news'],
      activeSection: 'news',
      pageType: 'news.index',
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(
        fetchNewsPageData(club.id, { teamId, category, month }, req),
      );
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { month, teamId, category } = this.props;
    const { month: prevMonth, teamId: prevTeamId, category: prevCategory } = prevProps;
    if (month !== prevMonth || teamId !== prevTeamId || category !== prevCategory) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const { hasLoaded, loadingError } = this.props;
    if (!hasLoaded && !loadingError) {
      this.load();
    }
  };

  load = () => {
    const {
      club: { id: clubId },
      loadPageData,
      month,
      teamId,
      category,
    } = this.props;
    loadPageData(clubId, { teamId, month, category });
  };

  handleChangeCategoryFilter = (category) => {
    const { month, teamId } = this.props;
    this.filterPage({ category, month, teamId });
  };

  handleChangeMonthFilter = (month) => {
    const { category, teamId } = this.props;
    this.filterPage({ category, month: month?.toFormat('yyyy-MM'), teamId });
  };

  handleChangeTeamFilter = (teamId) => {
    const { category, month } = this.props;
    this.filterPage({ category, month, teamId });
  };

  filterPage = (params) => {
    let route = 'news';

    if (params.teamId) {
      route += '.team';
    }

    if (params.category) {
      route += '.category';
    }

    if (params.month) {
      route += '.month';
    }

    const { club } = this.props;
    ClubRouter.pushClubRoute(club, route, params);
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} reload={this.load} />;
    }

    const { category, isLoading, messages, month, sections, t, teamId } = this.props;
    return (
      <News
        club={club}
        category={category}
        isLoading={isLoading}
        messages={messages}
        month={month}
        sections={sections}
        onChangeCategoryFilter={this.handleChangeCategoryFilter}
        onChangeMonthFilter={this.handleChangeMonthFilter}
        onChangeTeamFilter={this.handleChangeTeamFilter}
        t={t}
        teamId={teamId}
      />
    );
  }
}

NewsLandingPage.defaultProps = {
  category: null,
  loadingError: null,
  month: null,
  teamId: null,
};

NewsLandingPage.propTypes = {
  category: PropTypes.string,
  club: CLUB_PROP_TYPES.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadPageData: PropTypes.func.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
    }),
  ).isRequired,
  month: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      teams: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  teamId: PropTypes.number,
  t: PropTypes.func.isRequired,
};

const dispatchToProps = {
  loadPageData: fetchNewsPageData,
};

const mapStateToProps = (state, ownProps) => {
  const filters = {
    teamId: ownProps.teamId,
    category: ownProps.category,
    month: ownProps.month,
  };

  return {
    isLoading: isLoadingStream(state),
    hasLoaded: hasAttemptedLoad(state, ownProps.clubId, filters),
    loadingError: getNewsPageLoadingError(state),
    messages: getMessages(state, filters),
    sections: getClubSections(state, ownProps.clubId),
  };
};

export default connect(
  mapStateToProps,
  dispatchToProps,
)(withTranslation(['common', 'news'])(NewsLandingPage));
